import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { Firestore, DocumentReference, addDoc, collection } from '@angular/fire/firestore';
import { ClubStateDispatcher } from 'src/app/core/state/club/club.dispatcher';
import { ProfileService } from 'src/app/core/firebase/profile.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { GameSettingsService } from 'src/app/core/firebase/gameSettings.service';
import { GameThemeSettingsDispatcher } from 'src/app/core/state/gameThemeSettings/gameThemeSettings.dispatcher';
import { ClubService } from 'src/app/core/firebase/club.service';
import { ClubMessage } from 'src/app/core/state/club/club.state';
import { ChatUserInfoDispatcher } from 'src/app/core/state/chatUserInfo/chatUserInfo.dispatcher';
import { ComponentToLoad, GameStateId } from 'src/app/core/state/game/game.state';
import { SplitScreenService } from 'src/app/game/splitScreen.service';
import { Background } from 'src/app/core/state/gameThemeSettings/gameThemeSettings.state';
import { IChatUserInfoData } from 'src/app/core/state/chatUserInfo/chatUserInfo.state';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';
import { ClubChatService } from 'src/app/game/page/game/club-chat-container/services/club-chat-container.service';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';

@Component({
    selector: 'club-chat-box',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './club-chat-box.component.html',
    styleUrls: ['./club-chat-box.component.scss'],
    standalone: false
})
export class ClubChatBoxComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    @ViewChild('scrollMe') private myScrollContainer: ElementRef;
    @Input() inPage = false;
    @Input()
    @HostBinding('class.in-game')
    inGamePage = false;
    @Input() gameStateId: GameStateId;
    @Input() clubControlsForm = false;
    @Input() isHidden = false;
    clubId: string;
    myName: string;
    myId = '';
    messages: ClubMessage[] = [];
    showAvatar$: Observable<boolean>;
    myPicURL: string | number = 'assets/img/guest.jpg';
    messageInput = '';
    profileSubscription: Subscription;
    messageSubscription: Subscription;
    initialMessagesCount = 30;
    newMessageOnScroll = false;
    requestedChatUserInfoIds: string[] = [];
    background$: Observable<Background>;
    needSpaceForGameBox$: Observable<boolean>;
    selectedClubSubscription: Subscription;

    constructor(
        public clubDispatcher: ClubStateDispatcher,
        private clubService: ClubService,
        public firestore: Firestore,
        public profileservice: ProfileService,
        public profileService: ProfileStateDispatcher,
        private gameThemeSettingsDispatcher: GameThemeSettingsDispatcher,
        public ref: ChangeDetectorRef,
        private chatUserInfoDispatcher: ChatUserInfoDispatcher,
        public splitScreenService: SplitScreenService,
        private gameSettingsService: GameSettingsService,
        private gameStoreDispatcher: GameStoreDispatcher,
        @Optional() private clubChatService: ClubChatService,
        private sopoPopupService: SopoPopupService,
    ) {}

    ngOnInit() {
        this.initMessageCount();
        this.initializeObservables();
        this.initializeSubscriptions();
        if(!this.inGamePage) this.loadClubMessages();
    }

    initMessageCount() {
        if(this.inGamePage) this.initialMessagesCount = 11;
    }

    ngAfterViewInit() {
        this.myScrollContainer.nativeElement.onscroll = () => {
            this.onScroll();
        }
        if (this.inPage) {
            setTimeout(() => { //animation time declared in club lobby
                this.initializeMessageSubscriptionAndObservable();
            }, 250)
        } else {
            this.initializeMessageSubscriptionAndObservable();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isHidden.previousValue && !changes.isHidden.currentValue && this.messages[0]) {
            localStorage.setItem(this.clubId, this.messages[0].messageID);
        }
    }

    initializeObservables() {
        this.background$ = this.gameThemeSettingsDispatcher.getGameThemeSettingsState().pipe(
            map(settingsState => settingsState.background),
        );
        this.showAvatar$ = this.gameThemeSettingsDispatcher.getThemeOptions('showAvatars');
        this.needSpaceForGameBox$ = this.gameStoreDispatcher.getState().pipe(map((gameState) => gameState.gamesList.length > 0 && this.inPage && !this.inGamePage));
    }

    async initializeSubscriptions() {
        this.profileSubscription = this.profileService
            .getProfileState()
            .pipe(map(state => state.profileData))
            .subscribe((profileData) => {
                if (profileData !== null) {
                    this.myName = profileData.displayName;
                    this.myPicURL = profileData.photoURL;
                    this.myId = profileData.uid;
                    this.ref.markForCheck();
                }
            });
        if (this.inGamePage) {
            this.selectedClubSubscription = this.clubChatService.idSet$
                .subscribe((clubId) => {
                    this.clubId = clubId;
                    this.initialMessagesCount = 11;
                    this.clubChatService.limitTo = 11;
                    this.ref.detectChanges();
                })
        }
    }

    async loadClubMessages() {
        this.clubId = await this.clubDispatcher.getClubID();
        this.clubService.loadClubMessages(this.clubId);
    }

    initializeMessageSubscriptionAndObservable() {
        let messages$: Observable<ClubMessage[]>;
        if(!this.inGamePage) messages$ = this.clubDispatcher.getClubMessages();
        else messages$ = this.clubChatService.clubMessage$;

        this.messageSubscription = messages$
            .subscribe((messages => { 
                this.messages = messages;         
                let unrequestedChatAvatarUserIds: string[] = [];

                messages.forEach(message => {
                    if (!this.requestedChatUserInfoIds.includes(message.senderID)) {
                        unrequestedChatAvatarUserIds.push(message.senderID);
                        this.requestedChatUserInfoIds.push(message.senderID);
                    }
                });

                if (unrequestedChatAvatarUserIds.length) {
                    this.chatUserInfoDispatcher.requestForChatUserInfos(unrequestedChatAvatarUserIds);
                }

                if (messages.length) { 
                    if (this.isHidden) {
                        this.ref.detectChanges();
                        return;
                    }

                    if (localStorage.getItem(this.clubId) === messages[0].messageID) {
                        this.ref.detectChanges();
                        return;
                    }
                    localStorage.setItem(this.clubId, messages[0].messageID);

                    if (this.myScrollContainer.nativeElement.scrollTop <= -10 && messages[0].senderID != this.myId) {
                        this.newMessageOnScroll = true;
                        this.ref.detectChanges();
                        return;
                    }
                }

                this.scrollToBottom();

                this.ref.detectChanges();
            }));
    }

    async sendMessage() {
        const clubId = this.clubId;
        if (this.messageInput.trim().length > 0) {
            const message = this.messageInput;

            this.sendClubMessage(clubId, this.messageInput, this.myName, this.myPicURL.toString(), this.myId)
                .catch(async () => {
                    this.sopoPopupService.showAlert('app', {
                        header: 'Your message could not be sent.',
                        message: 'There was an error sending your message. Please try again.',
                        dismissAfter: 5000,
                    });

                    this.messageInput = message;
                    this.ref.markForCheck();
                })
        }
        this.messageInput = '';
        this.ref.detectChanges();
    }

    sendClubMessage(
        clubID: string,
        message: string,
        senderName: string,
        senderPic: string,
        senderID: string,
    ): Promise<DocumentReference> {
        const collReference = collection(this.firestore, `clubs/${clubID}/messages`);
        return addDoc(collReference, {
            text: message,
            senderName: senderName,
            senderPic: senderPic,
            dateCreated: new Date(),
            senderID,
        });
    }

    onScroll() {
        if (this.newMessageOnScroll && this.myScrollContainer.nativeElement.scrollTop > -10) {
            this.newMessageOnScroll = false;
            this.ref.markForCheck();
        }
    }

    scrollToBottom(): void {
        if (this.newMessageOnScroll) this.newMessageOnScroll = false;
        try {
            this.myScrollContainer.nativeElement.scrollTop = 0;
        } catch (err) {}
    }

    loadMoreMessage() {
        if(this.inGamePage) { 
            this.loadMoreMessagesInGame();
            return;
        }
        let loadMore = 30;
        this.initialMessagesCount += loadMore;
        this.clubService.loadClubMessages(this.clubId, this.initialMessagesCount);
    }

    loadMoreMessagesInGame() {
        let loadMore = 11;
        this.initialMessagesCount += loadMore;
        this.clubChatService.limitTo = this.initialMessagesCount;
    }

    trackById(index: number, message: ClubMessage): string {
        return message.messageID;
    }

    getChatUserInfoObservable(userId: string): Observable<IChatUserInfoData> {
        return this.chatUserInfoDispatcher.getChatUserInfoObservable(userId);
    }

    needInGamePadding(isGridView: boolean): boolean {
        if (!this.inGamePage) return false;
        if (isGridView) return false;
        return true;
    }

    needOffSet(gamesList: ComponentToLoad[]) {
        if(gamesList[0].gameStateId === this.gameStateId || gamesList[1].gameStateId === this.gameStateId) {
            if(gamesList.length === 4) return 'max';
            return 'edge';
        } 
        return '';
    }

    getBackgroundStyle(background: Background) {
        if (!this.inGamePage) return {};
        return this.gameSettingsService.getBackground(background);
    }

    ngOnDestroy(): void {
        if (this.profileSubscription) this.profileSubscription.unsubscribe();
        if (this.messageSubscription) this.messageSubscription.unsubscribe();
        if (this.requestedChatUserInfoIds.length)
            this.chatUserInfoDispatcher.clearRequestForChatUserInfos(this.requestedChatUserInfoIds);
        if (this.selectedClubSubscription) this.selectedClubSubscription.unsubscribe();
    }
}
